import React, { useState, useRef } from 'react';
import RecordRTC from 'recordrtc';
import { Play, Square, Camera, Mic, CheckCircle } from 'lucide-react';

const CameraTest = () => {
  const [cameraReady, setCameraReady] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [videoBlob, setVideoBlob] = useState(null);
  const [testResults, setTestResults] = useState([]);
  
  const videoRef = useRef(null);
  const audioRecorderRef = useRef(null);
  const videoRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const addTestResult = (test, success, message) => {
    setTestResults(prev => [...prev, { test, success, message, timestamp: new Date().toLocaleTimeString() }]);
  };

  const startCamera = async () => {
    try {
      addTestResult('Camera Access', null, 'Requesting camera and microphone permissions...');
      
      const constraints = {
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user'
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      addTestResult('Camera Access', true, `✅ Camera access granted! Video tracks: ${stream.getVideoTracks().length}, Audio tracks: ${stream.getAudioTracks().length}`);
      
      // Store stream reference
      streamRef.current = stream;

      // Display video preview
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        addTestResult('Video Preview', true, '✅ Video preview started successfully');
        setCameraReady(true);
      }

      // Log track details
      stream.getTracks().forEach((track, index) => {
        addTestResult('Track Info', true, `Track ${index + 1}: ${track.kind} - ${track.label} - readyState: ${track.readyState}`);
      });

    } catch (error) {
      addTestResult('Camera Access', false, `❌ Camera access failed: ${error.name} - ${error.message}`);
      console.error('Camera error:', error);
    }
  };

  const startRecording = async () => {
    if (!streamRef.current) {
      addTestResult('Recording', false, '❌ No camera stream available');
      return;
    }

    try {
      addTestResult('RecordRTC Setup', null, 'Creating RecordRTC instances...');
      
      // Create RecordRTC video recorder
      const videoRecorder = new RecordRTC(streamRef.current, {
        type: 'video',
        mimeType: 'video/webm;codecs=vp8,opus',
        audioBitsPerSecond: 128000,
        videoBitsPerSecond: 1000000,
        canvas: {
          width: 640,
          height: 480
        },
        frameInterval: 90
      });

      // Create RecordRTC audio recorder
      const audioRecorder = new RecordRTC(streamRef.current, {
        type: 'audio',
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 128000,
        numberOfAudioChannels: 1
      });

      videoRecorderRef.current = videoRecorder;
      audioRecorderRef.current = audioRecorder;

      addTestResult('RecordRTC Setup', true, '✅ RecordRTC instances created successfully');

      // Start recording
      videoRecorder.startRecording();
      audioRecorder.startRecording();
      
      addTestResult('Recording Start', true, '✅ Recording started with RecordRTC');
      setRecording(true);

      // Start timer
      setRecordingTime(0);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      addTestResult('Recording Start', false, `❌ Recording failed: ${error.message}`);
      console.error('Recording error:', error);
    }
  };

  const stopRecording = () => {
    if (!recording) return;

    try {
      addTestResult('Recording Stop', null, 'Stopping RecordRTC recording...');
      
      // Stop timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      // Stop RecordRTC recorders
      if (videoRecorderRef.current) {
        videoRecorderRef.current.stopRecording(() => {
          const videoBlob = videoRecorderRef.current.getBlob();
          setVideoBlob(videoBlob);
          addTestResult('Video Recording', true, `✅ Video recording completed: ${videoBlob.size} bytes`);
        });
      }
      
      if (audioRecorderRef.current) {
        audioRecorderRef.current.stopRecording(() => {
          const audioBlob = audioRecorderRef.current.getBlob();
          setAudioBlob(audioBlob);
          addTestResult('Audio Recording', true, `✅ Audio recording completed: ${audioBlob.size} bytes`);
        });
      }

      setRecording(false);
      addTestResult('Recording Stop', true, '✅ Recording stopped successfully');

    } catch (error) {
      addTestResult('Recording Stop', false, `❌ Error stopping recording: ${error.message}`);
      console.error('Stop recording error:', error);
    }
  };

  const cleanup = () => {
    try {
      // Stop and cleanup RecordRTC recorders
      if (audioRecorderRef.current) {
        try {
          if (audioRecorderRef.current.getState() === 'recording') {
            audioRecorderRef.current.stopRecording();
          }
          audioRecorderRef.current.destroy();
        } catch (e) {
          console.warn('Error cleaning up audio recorder:', e);
        }
        audioRecorderRef.current = null;
      }
      
      if (videoRecorderRef.current) {
        try {
          if (videoRecorderRef.current.getState() === 'recording') {
            videoRecorderRef.current.stopRecording();
          }
          videoRecorderRef.current.destroy();
        } catch (e) {
          console.warn('Error cleaning up video recorder:', e);
        }
        videoRecorderRef.current = null;
      }
      
      // Stop existing media stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => {
          track.stop();
        });
        streamRef.current = null;
      }
      
      // Clear video preview
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      setCameraReady(false);
      setRecording(false);
      setRecordingTime(0);
      setAudioBlob(null);
      setVideoBlob(null);
      
      addTestResult('Cleanup', true, '✅ All resources cleaned up successfully');
      
    } catch (error) {
      addTestResult('Cleanup', false, `❌ Cleanup error: ${error.message}`);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          RecordRTC Camera Test
        </h1>
        <p className="text-gray-600">
          Direct test of camera and recording functionality using RecordRTC
        </p>
      </div>

      {/* Video Preview */}
      <div className="mb-8">
        <div className="relative bg-gray-900 rounded-lg overflow-hidden mx-auto" style={{ width: '640px', height: '480px' }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
            style={{ transform: 'scaleX(-1)' }}
          />
          
          {/* Recording Indicator */}
          {recording && (
            <div className="absolute top-4 left-4 flex items-center space-x-2">
              <div className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center">
                <div className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></div>
                REC {formatTime(recordingTime)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="text-center mb-8">
        <div className="flex justify-center space-x-4">
          {!cameraReady ? (
            <button
              onClick={startCamera}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300 transition-colors flex items-center"
            >
              <Camera className="mr-2 h-5 w-5" />
              Start Camera
            </button>
          ) : (
            <>
              {!recording ? (
                <button
                  onClick={startRecording}
                  className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 focus:outline-none focus:ring-4 focus:ring-green-300 transition-colors flex items-center"
                >
                  <Play className="mr-2 h-5 w-5" />
                  Start Recording
                </button>
              ) : (
                <button
                  onClick={stopRecording}
                  className="bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 focus:outline-none focus:ring-4 focus:ring-red-300 transition-colors flex items-center"
                >
                  <Square className="mr-2 h-5 w-5" />
                  Stop Recording
                </button>
              )}
              
              <button
                onClick={cleanup}
                className="bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-700 focus:outline-none focus:ring-4 focus:ring-gray-300 transition-colors"
              >
                Cleanup
              </button>
            </>
          )}
        </div>
      </div>

      {/* Download Recorded Files */}
      {(audioBlob || videoBlob) && (
        <div className="text-center mb-8">
          <h3 className="text-lg font-semibold mb-4">Recorded Files</h3>
          <div className="flex justify-center space-x-4">
            {videoBlob && (
              <button
                onClick={() => downloadBlob(videoBlob, 'test-video.webm')}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
              >
                Download Video ({Math.round(videoBlob.size / 1024)}KB)
              </button>
            )}
            {audioBlob && (
              <button
                onClick={() => downloadBlob(audioBlob, 'test-audio.webm')}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Download Audio ({Math.round(audioBlob.size / 1024)}KB)
              </button>
            )}
          </div>
        </div>
      )}

      {/* Test Results */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Test Results</h3>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {testResults.map((result, index) => (
            <div key={index} className="flex items-center space-x-3 text-sm">
              <span className="text-gray-500 font-mono">{result.timestamp}</span>
              <span className="font-medium">{result.test}:</span>
              {result.success === true && <CheckCircle className="h-4 w-4 text-green-500" />}
              {result.success === false && <span className="text-red-500">❌</span>}
              {result.success === null && <span className="text-yellow-500">⏳</span>}
              <span className={result.success === false ? 'text-red-600' : result.success === true ? 'text-green-600' : 'text-yellow-600'}>
                {result.message}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CameraTest;